/**
 * SDZ Pulse — storefront motion + studio backdrop tagging.
 *
 * 1) Scroll-triggered `.sdz-inview` reveals (skipped if prefers-reduced-motion).
 * 2) Tags white-studio product photos with `.sdz-studio` so CSS can multiply
 *    them onto a dark charcoal stage (lifestyle/UGC stays `.sdz-lifestyle`).
 */
(function () {
  if (typeof document === "undefined") return;

  const root = document.documentElement;

  // ── Header nav: Collection as real sibling + canonical hrefs ─────────────
  // Horizon desktop items use flex-column <li>s. Injecting an <a> inside Home
  // stacks "Home / The Collection". Always insert a sibling <li>.
  function patchHeaderNav() {
    const header = document.querySelector("header");
    if (!header) return;

    // Remove broken in-Home injects from older builds (anchor inside Home <li>)
    header.querySelectorAll("a[data-sdz-collection-link='1']").forEach((a) => {
      const li = a.closest("li");
      if (li && li.querySelectorAll("a").length > 1) {
        a.remove();
        return;
      }
      // orphan inject li without proper structure — keep if it's already a sibling
      if (li && li.getAttribute("data-sdz-collection-link") === "1") return;
      a.remove();
    });

    const titleOf = (el) =>
      (el?.textContent || "").replace(/\s+/g, " ").trim();

    const alreadyHasCollection = (list) =>
      [...list.children].some((li) => {
        const t = titleOf(li.querySelector(".menu-list__link-title, .menu-drawer__menu-item-text, a"));
        return /^the\s*collection$/i.test(t.split("\n")[0] || t);
      });

    const insertCollectionSibling = (homeLi, variant) => {
      const list = homeLi.parentElement;
      if (!list || alreadyHasCollection(list)) return;
      if (list.querySelector("li[data-sdz-collection-link='1']")) return;

      const li = document.createElement("li");
      li.setAttribute("data-sdz-collection-link", "1");

      if (variant === "drawer") {
        li.className = "menu-drawer__list-item";
        li.innerHTML =
          '<a href="/collections/the-collection" class="menu-drawer__menu-item menu-drawer__menu-item--mainlist" data-sdz-collection-link="1">' +
          '<span class="menu-drawer__menu-item-text wrap-text">The Collection</span></a>';
      } else {
        li.className = "menu-list__list-item";
        li.innerHTML =
          '<a href="/collections/the-collection" class="menu-list__link" data-sdz-collection-link="1" data-skip-node-update="true">' +
          '<span class="menu-list__link-title">The Collection</span></a>';
      }

      list.insertBefore(li, homeLi.nextSibling);
    };

    // Desktop Horizon row
    const desktopHome = [...header.querySelectorAll(".menu-list__list-item")].find((li) => {
      const t = titleOf(li.querySelector(":scope > .menu-list__link .menu-list__link-title, :scope > .menu-list__link"));
      return /^home$/i.test(t);
    });
    if (desktopHome) insertCollectionSibling(desktopHome, "desktop");

    // Mobile drawer
    const drawerHome = [...header.querySelectorAll(".menu-drawer__list-item")].find((li) => {
      const t = titleOf(li.querySelector(".menu-drawer__menu-item-text, a"));
      return /^home$/i.test(t);
    });
    if (drawerHome) insertCollectionSibling(drawerHome, "drawer");

    const CANONICAL = {
      home: "/",
      "the collection": "/collections/the-collection",
      street: "/collections/pulse-street",
      lounge: "/collections/pulse-lounge",
      active: "/collections/pulse-active",
      hats: "/collections/pulse-hats",
      "why us": "/pages/why-us",
      about: "/pages/why-us",
      contact: "/pages/contact",
    };

    const topLinks = [...header.querySelectorAll("a")].filter((a) => {
      const t = titleOf(a);
      if (!t || t.length > 24) return false;
      if (a.closest("[class*='mega'], [class*='submenu'], [class*='dropdown']") && !a.className.includes("menu-list__link") && !a.className.includes("menu-drawer__menu-item")) {
        return false;
      }
      return Object.prototype.hasOwnProperty.call(CANONICAL, t.toLowerCase()) || /^the\s*collection$/i.test(t);
    });

    for (const a of topLinks) {
      const key = titleOf(a).toLowerCase();
      const href = CANONICAL[key];
      if (href) a.setAttribute("href", href);
    }
  }

  function scheduleNavPatch() {
    patchHeaderNav();
    setTimeout(patchHeaderNav, 400);
    setTimeout(patchHeaderNav, 1200);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", scheduleNavPatch, { once: true });
  } else {
    scheduleNavPatch();
  }
  window.addEventListener("load", scheduleNavPatch, { once: true });

  // ── Footer email signup → Microsoft list + PULSE10 welcome ───────────────
  function mailingListEndpoint() {
    return "https://api.sdzautomation.com/api/v1/affiliate/public/mailing-list/subscribe";
  }

  function findSignupEmail(form) {
    const input =
      form.querySelector('input[type="email"]')
      || form.querySelector('input[name="contact[email]"]')
      || form.querySelector('input[name="email"]')
      || form.querySelector('input[autocomplete="email"]');
    return String(input?.value || "").trim().toLowerCase();
  }

  function looksLikeEmailSignup(form) {
    if (!form || form.dataset.sdzMailBridge === "1") return false;
    const action = String(form.getAttribute("action") || "").toLowerCase();
    const hasEmail = Boolean(
      form.querySelector('input[type="email"], input[name="contact[email]"], input[name="email"]'),
    );
    if (!hasEmail) return false;
    if (/contact|customer|newsletter|email/.test(action)) return true;
    if (form.closest("footer, [id*='footer'], [class*='footer'], [class*='email-signup'], email-signup")) {
      return true;
    }
    return Boolean(form.querySelector("[name='form_type'][value='customer'], input[name='contact[tags]']"));
  }

  async function postMailingList(email) {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
    try {
      await fetch(mailingListEndpoint(), {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ email, website: "" }),
        mode: "cors",
        credentials: "omit",
        keepalive: true,
      });
    } catch {
      // Native Shopify submit still proceeds; sync can retry later.
    }
  }

  function bridgeEmailSignups() {
    const forms = [...document.querySelectorAll("form")].filter(looksLikeEmailSignup);
    for (const form of forms) {
      form.dataset.sdzMailBridge = "1";
      form.addEventListener(
        "submit",
        () => {
          const email = findSignupEmail(form);
          if (email) postMailingList(email);
        },
        { capture: true },
      );
    }
  }

  function scheduleMailBridge() {
    bridgeEmailSignups();
    setTimeout(bridgeEmailSignups, 600);
    setTimeout(bridgeEmailSignups, 1800);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", scheduleMailBridge, { once: true });
  } else {
    scheduleMailBridge();
  }
  window.addEventListener("load", scheduleMailBridge, { once: true });

  // ── Studio backdrop tagging (always on) ──────────────────────────────────
  function looksLikeLifestyle(img) {
    const blob = `${img.getAttribute("alt") || ""} ${img.currentSrc || img.src || ""}`.toLowerCase();
    return /ugc|lifestyle|on-model|runway|pulse-ugc|worn|as-worn/.test(blob);
  }

  function sampleIsStudioWhite(img) {
    try {
      if (!img.naturalWidth || !img.complete) return null;
      const canvas = document.createElement("canvas");
      const size = 24;
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      if (!ctx) return null;
      ctx.drawImage(img, 0, 0, size, size);
      const data = ctx.getImageData(0, 0, size, size).data;
      // Sample corners + edge midpoints — studio flats are white there.
      const points = [
        0,
        size - 1,
        (size - 1) * size,
        size * size - 1,
        Math.floor(size / 2),
        (Math.floor(size / 2) * size),
        (Math.floor(size / 2) * size) + (size - 1),
        ((size - 1) * size) + Math.floor(size / 2),
      ];
      let whiteHits = 0;
      for (const p of points) {
        const i = p * 4;
        if (data[i] > 232 && data[i + 1] > 232 && data[i + 2] > 232) whiteHits += 1;
      }
      return whiteHits >= 5;
    } catch {
      // CORS / tainted canvas — fall back to filename heuristics.
      return null;
    }
  }

  function tagImage(img) {
    if (!img || img.dataset.sdzStudioTagged === "1") return;
    img.dataset.sdzStudioTagged = "1";

    const host =
      img.closest(".product-media") ||
      img.closest(".product__media") ||
      img.closest(".sdz-feat__media") ||
      img.closest(".card__media") ||
      img.closest(".product-card__image") ||
      img.parentElement;

    if (looksLikeLifestyle(img)) {
      img.classList.add("sdz-lifestyle");
      if (host) host.classList.add("sdz-lifestyle");
      return;
    }

    const sampled = sampleIsStudioWhite(img);
    const printfulHint = /front-|back-|printful|ghost|mockup|premium-short|hoodie-|sweat|tee-|cap-/i.test(
      `${img.currentSrc || img.src || ""}`,
    );
    const isStudio = sampled === true || (sampled === null && printfulHint);

    if (isStudio) {
      img.classList.add("sdz-studio");
      if (host) host.classList.add("sdz-studio");
    } else {
      img.classList.add("sdz-lifestyle");
      if (host) host.classList.add("sdz-lifestyle");
    }
  }

  function tagAll() {
    const imgs = document.querySelectorAll(
      ".product-media img, .product__media img, media-gallery img, .sdz-feat__media img, .card__media img, .product-card__image img",
    );
    imgs.forEach((img) => {
      if (img.complete && img.naturalWidth) tagImage(img);
      else img.addEventListener("load", () => tagImage(img), { once: true });
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", tagAll, { once: true });
  } else {
    tagAll();
  }
  window.addEventListener("load", tagAll, { once: true });
  // Variant swaps often replace gallery images.
  setTimeout(tagAll, 1200);
  setTimeout(tagAll, 3200);

  // ── Scroll motion (respect reduced-motion) ───────────────────────────────
  const reduced = (() => {
    try {
      return window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    } catch {
      return false;
    }
  })();

  if (reduced) return;

  root.dataset.sdzPulseMotion = "on";

  // ── Hero EKG: stay with viewport while scrolling, keep traveling pulse ───
  function initEkgScrollFollow() {
    const wrap = document.querySelector(".sdz-pulse-hero__ekg-wrap");
    if (!wrap || !document.querySelector(".sdz-pulse-hero")) return;

    // Horizon scrolls inside `.page-wrapper`, and the hero sets
    // `will-change: transform` which traps `position: fixed`. Portal the
    // EKG to <body> so it stays on-screen and keeps pulsing across.
    if (wrap.parentElement !== document.body) {
      const hero = document.querySelector(".sdz-pulse-hero");
      const signal =
        (hero && getComputedStyle(hero).getPropertyValue("--ph-signal").trim()) ||
        "#E11D2E";
      wrap.style.setProperty("--ph-signal", signal);
      wrap.setAttribute("data-sdz-ekg-portaled", "1");
      document.body.appendChild(wrap);
    }

    const scroller =
      document.querySelector(".page-wrapper") ||
      document.scrollingElement ||
      document.documentElement;

    const readY = () => {
      if (scroller === document.body || scroller === document.documentElement) {
        return window.scrollY || window.pageYOffset || 0;
      }
      return scroller.scrollTop || 0;
    };

    const readMax = (vh) => {
      const scrollH =
        scroller === document.body || scroller === document.documentElement
          ? Math.max(
              document.body?.scrollHeight || 0,
              document.documentElement?.scrollHeight || 0,
            )
          : scroller.scrollHeight || 0;
      return Math.max(1, scrollH - vh);
    };

    let ticking = false;

    const update = () => {
      ticking = false;
      const y = readY();
      const vh = window.innerHeight || 1;
      const max = readMax(vh);
      const progress = Math.min(1, Math.max(0, y / max));

      // Soft vertical wander + gentle horizontal drift with scroll
      const top = 40 + progress * 10; // 40vh → 50vh
      const x = (progress - 0.35) * 56;

      // Strong in hero, still visible through mid-page, fade before footer
      const fadeStart = vh * 0.35;
      const fadeEnd = Math.max(fadeStart + 1, vh * 3.4);
      let opacity = 0.78;
      if (y > fadeStart) {
        opacity = Math.max(0, 0.78 * (1 - (y - fadeStart) / (fadeEnd - fadeStart)));
      }

      root.style.setProperty("--sdz-ekg-top", `${top.toFixed(2)}vh`);
      root.style.setProperty("--sdz-ekg-x", `${x.toFixed(1)}px`);
      root.style.setProperty("--sdz-ekg-y", "0px");
      root.style.setProperty("--sdz-ekg-opacity", opacity.toFixed(3));
      wrap.style.visibility = opacity < 0.05 ? "hidden" : "visible";
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    };

    update();
    scroller.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initEkgScrollFollow, { once: true });
  } else {
    initEkgScrollFollow();
  }

  const selectors = [
    ".hero-wrapper",
    ".sdz-pulse-intro__copy",
    ".sdz-pulse-ugc__head",
    ".sdz-pulse-ugc__tile",
    ".product",
    ".product__media-wrapper",
    "media-gallery",
    ".product-information",
    ".product__info-container",
    ".product__info-wrapper",
    ".sdz-story",
    ".sdz-story__card",
    ".sdz-feat",
    ".sdz-feat__card",
    ".sdz-lanes",
    ".sdz-lanes__lane",
    ".sdz-ugc",
    ".sdz-ugc__tile",
    ".sdz-manifesto",
  ];

  const nodes = [];
  const seen = new Set();
  for (const sel of selectors) {
    const list = document.querySelectorAll(sel);
    for (const el of list) {
      if (!el || seen.has(el)) continue;
      seen.add(el);
      nodes.push(el);
    }
  }

  nodes.forEach((el, i) => {
    const delay = Math.min(i, 24) * 70;
    el.style.setProperty("--sdz-delay-ms", `${delay}ms`);
  });

  const vh = window.innerHeight || 900;
  const vw = window.innerWidth || 1440;
  const inViewport = (el) => {
    try {
      const r = el.getBoundingClientRect();
      return r.bottom >= vh * 0.18 && r.top <= vh * 0.88 && r.right >= 0 && r.left <= vw;
    } catch {
      return false;
    }
  };

  nodes.forEach((el) => {
    if (inViewport(el)) el.classList.add("sdz-inview");
  });

  if (!("IntersectionObserver" in window)) return;

  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) entry.target.classList.add("sdz-inview");
      }
    },
    { threshold: 0.15, rootMargin: "0px 0px -10% 0px" },
  );

  nodes.forEach((el) => io.observe(el));
})();
